import { useState, useCallback, type ChangeEvent } from "react";
import "./NotesView.css";

interface Note {
    text: string;
}

interface NotesViewProps {
    notes: string[];
    onNotesChange: (notes: string[]) => void;
    maxNotes?: number;
    maxLength?: number;
    title?: string;
    placeholder?: string;
    readOnly?: boolean;
}

export function NotesView({
    notes,
    onNotesChange,
    maxNotes = 5,
    maxLength = 70,
    title = "Bemerkungen",
    placeholder = "Neue Bemerkung eingeben...",
    readOnly = false
}: NotesViewProps) {
    const [newNoteInput, setNewNoteInput] = useState("");

    const handleAddNote = useCallback(() => {
        const trimmedNote = newNoteInput.trim();
        if (!trimmedNote) return;

        // Validate max length
        if (trimmedNote.length > maxLength) {
            return; // Silently ignore if too long
        }

        const updatedNotes = [...notes];
        if (updatedNotes.length >= maxNotes) {
            // Rotate: remove oldest (first), add newest at end
            updatedNotes.shift();
        }
        updatedNotes.push(trimmedNote);
        onNotesChange(updatedNotes);
        setNewNoteInput("");
    }, [newNoteInput, notes, onNotesChange, maxNotes, maxLength]);

    const handleDeleteNote = useCallback((indexToDelete: number) => {
        const updatedNotes = notes.filter((_, index) => index !== indexToDelete);
        onNotesChange(updatedNotes);
    }, [notes, onNotesChange]);

    return (
        <div className="notes-view-container">
            <h3 className="notes-view-title">{title}</h3>
            <div className="notes-view-chat">
                {notes.length === 0 ? (
                    <div className="notes-view-empty">
                        Keine Bemerkungen vorhanden
                    </div>
                ) : (
                    notes.map((note, index) => (
                        <div key={index} className="notes-view-bubble">
                            <span className="notes-view-text">{note}</span>
                            {!readOnly && (
                                <button
                                    type="button"
                                    className="notes-view-delete"
                                    onClick={() => handleDeleteNote(index)}
                                    title="Notiz löschen"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    ))
                )}
            </div>
            {!readOnly && notes.length < maxNotes && (
                <div className="notes-view-input-container">
                    <input
                        type="text"
                        className="notes-view-input"
                        value={newNoteInput}
                        maxLength={maxLength}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setNewNoteInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleAddNote();
                            }
                        }}
                        placeholder={`${placeholder} (max. ${maxLength} Zeichen)...`}
                    />
                    <button
                        type="button"
                        className="notes-view-add-btn"
                        onClick={handleAddNote}
                    >
                        Hinzufügen
                    </button>
                </div>
            )}
        </div>
    );
}
